import type { ProjectDetailsScene, ProjectDetailsStoryboardShot } from "@/types/project";

export function findStoryboardShotById(
  shots: readonly ProjectDetailsStoryboardShot[] | undefined,
  shotId: string | undefined
): ProjectDetailsStoryboardShot | undefined {
  if (!shots?.length || !shotId) {
    return undefined;
  }

  return shots.find((s) => s.id === shotId);
}

/** User-facing shot title (`shot-description`), with fallbacks. */
export function storyboardShotTitle(
  shot: Pick<ProjectDetailsStoryboardShot, "shot-description" | "shot-name" | "shot-id"> | null | undefined
): string {
  if (!shot) {
    return "";
  }

  const description = shot["shot-description"]?.trim();
  if (description) {
    return description;
  }

  const name = shot["shot-name"]?.trim();
  if (name) {
    return name;
  }

  const shotId = shot["shot-id"]?.trim();
  if (shotId) {
    return shotId;
  }

  return "";
}

export function displayNameForShotImageGeneration(shot: ProjectDetailsStoryboardShot | null | undefined): string {
  const title = storyboardShotTitle(shot);
  return title || `Frame ${Date.now()}`;
}

export function displayNameForVideoGeneration(shot: ProjectDetailsStoryboardShot | null | undefined): string {
  const title = storyboardShotTitle(shot);
  return title ? `video-${title}` : `Video ${Date.now()}`;
}

export function transactionLabelForShotOrVideoGeneration(
  transaction: {
    readonly tag_type?: string | null;
    readonly tag_id?: string | null;
    readonly transaction_type: string;
  },
  shots: readonly ProjectDetailsStoryboardShot[] | undefined
): string | null {
  const tagType = transaction.tag_type?.trim().toLowerCase();
  const tagId = transaction.tag_id?.trim();
  if (!tagId || (tagType !== "shot" && tagType !== "video")) {
    return null;
  }

  const shot = findStoryboardShotById(shots, tagId);
  const title = storyboardShotTitle(shot);
  if (!title) {
    return null;
  }

  if (tagType === "video") {
    return `video-${title}`;
  }

  return title;
}

/** Non-empty storyboard shot `notes` string, or "" if missing / not a string. */
export function storyboardShotNotes(shot: ProjectDetailsStoryboardShot | Record<string, unknown> | null | undefined): string {
  if (!shot || typeof shot !== "object") {
    return "";
  }

  const raw = "notes" in shot ? shot["notes"] : undefined;
  return typeof raw === "string" ? raw.trim() : "";
}

/** Resolve notes from a shot list by id first, then by index. */
export function resolveStoryboardShotNotes(
  shots: readonly ProjectDetailsStoryboardShot[] | undefined,
  shotId: string | undefined,
  shotIndex: number
): string {
  if (!shots?.length) {
    return "";
  }

  if (shotId) {
    const fromId = storyboardShotNotes(shots.find((s) => s.id === shotId));
    if (fromId) {
      return fromId;
    }
  }

  if (shotIndex >= 0 && shotIndex < shots.length) {
    return storyboardShotNotes(shots[shotIndex]);
  }

  return "";
}

/**
 * First non-empty notes among multiple shot lists.
 * Pass fresher sources first (e.g. React Query cache before MobX store).
 */
export function resolveStoryboardShotNotesFromSources(
  shotId: string | undefined,
  shotIndex: number,
  ...shotLists: Array<readonly ProjectDetailsStoryboardShot[] | undefined>
): string {
  for (const shots of shotLists) {
    const notes = resolveStoryboardShotNotes(shots, shotId, shotIndex);
    if (notes) {
      return notes;
    }
  }

  return "";
}

/** Prefer local lists, then optional API shot payload (e.g. GET /shot/{id}). */
export function resolveStoryboardShotNotesForImagePrompt(
  shotId: string | undefined,
  shotIndex: number,
  shotLists: ReadonlyArray<readonly ProjectDetailsStoryboardShot[] | undefined>,
  apiShot?: ProjectDetailsStoryboardShot | Record<string, unknown> | null
): string {
  const fromLists = resolveStoryboardShotNotesFromSources(shotId, shotIndex, ...shotLists);
  if (fromLists) {
    return fromLists;
  }

  return storyboardShotNotes(apiShot);
}

/** Scene title for a shot’s `scene-id` (used in stored video visual metadata). */
export function sceneTitleForShotSceneId(scenes: readonly ProjectDetailsScene[] | undefined, sceneId: string | undefined): string {
  if (!scenes?.length || sceneId === undefined || sceneId === "") {
    return "";
  }

  const scene = scenes.find((s) => s["scene-id"] === sceneId);
  return scene?.["scene-title"]?.trim() ?? "";
}

/** Composite storyboard shot id stored on each shot (`{scene-id}-{shot-id}`). */
/** Display label only, e.g. for toasts - `.id` is a separate, permanent, opaque
 * value that never derives from `scene-id`/`shot-id` (see `nextStoryboardShotIdInScene`). */
export function storyboardShotCompositeId(sceneId: string, shotId: string): string {
  return `${sceneId}-${shotId}`;
}

/**
 * Product rule: no two shots may display the same `scene-id`/`shot-id` label pair.
 * Returns the conflicting label when another shot already shows it, or `null` when
 * the values are available. Purely a field-equality check - unrelated to `.id`.
 */
export function findStoryboardShotIdentityConflict(
  shots: readonly ProjectDetailsStoryboardShot[],
  sceneId: string,
  shotId: string,
  excludeShotId: string
): string | null {
  for (const shot of shots) {
    if (shot.id === excludeShotId) {
      continue;
    }

    if (shot["scene-id"] === sceneId && shot["shot-id"] === shotId) {
      return storyboardShotCompositeId(sceneId, shotId);
    }
  }

  return null;
}

/** Rename a shot's displayed scene/shot label. `.id` never changes - it's an opaque
 * internal handle (route params, generation `tag_id` correlation, `visuals[].storyboard_shot_id`)
 * assigned once at creation, not derived from these display fields. */
export function patchStoryboardShotIdentity(
  shot: ProjectDetailsStoryboardShot,
  sceneId: string,
  shotId: string
): ProjectDetailsStoryboardShot {
  return {
    ...shot,
    "scene-id": sceneId,
    "shot-id": shotId,
    "shot-name": shotId
  };
}

/** Best-effort shot number from `shot-id` (e.g. `shot-3` → 3). */
export function shotNumberFromShotId(shotId: string | undefined): number {
  if (shotId === undefined || shotId === "") {
    return 1;
  }

  const m = shotId.match(/(\d+)/);
  if (m?.[1] === undefined) {
    return 1;
  }

  const n = Number.parseInt(m[1], 10);
  return Number.isFinite(n) ? n : 1;
}
